import { IChecklist, IChecklistParts, IChecklistPopulated } from "@mctypes/checklist.types";
import { IId } from "@mctypes/global.types";
import { IAddChecklistBody, IPostBodyCheclistImportExcel } from "@schemas/checklistSchema";
import "./checklistapi.enhance";
import { apiSlice } from "../../../apiSlice";
import { setShowFailedMessageThunk } from "../../../global/globalSlice";
import { sortArrayByNumber } from "../../utils";
import {
  setChecklistCategoriesInRedux,
  setChecklistItemsInRedux,
  setCurrentChecklistInRedux,
  updateChecklistProgressInRedux,
} from "./checklistSlice";

export interface IChecklistGetAllQueryInput {
  projectId: string;
  freeTextSearch?: string;
  doksamDocId?: string;
}

export interface IChecklistImportExcelHttp {
  params: {
    projectId: string;
  };
  body: IPostBodyCheclistImportExcel;
}

export const checklistRTK = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjectChecklists: builder.query<IChecklistPopulated[], IChecklistGetAllQueryInput>({
      query: (input) => {
        return {
          url: `/project/${input.projectId}/checklist/all`,
          method: "POST",
          body: {
            freeTextSearch: input.freeTextSearch,
            doksamDocId: input.doksamDocId,
          },
        };
      },
      providesTags: ["Checklist"],
    }),
    getProjectChecklistTemplates: builder.query<IChecklistPopulated[], { projectId: string; freeTextSearch?: string }>({
      query: (input) => {
        return {
          url: `/project/${input.projectId}/checklist/all`,
          method: "POST",
          body: {
            freeTextSearch: input.freeTextSearch,
            isTemplate: true,
          },
        };
      },
      providesTags: ["Checklist"],
    }),
    getSingleChecklist: builder.query<IChecklistPopulated, { projectId: string; checklistId: string | number }>({
      query: (input) => {
        return {
          url: `/project/${input.projectId}/checklist/instance/${input.checklistId}`,
          method: "GET",
        };
      },
      providesTags: ["Checklist"],
    }),
    getUniqueChecklistTags: builder.query<IId[], { projectId: string }>({
      query: (input) => {
        return {
          url: `/project/${input.projectId}/checklist/tags`,
          method: "GET",
        };
      },
      providesTags: ["Checklist"],
    }),
    getActiveProjectChecklistsCreatedByLoggedInUser: builder.query<IChecklist[], { userId: string; projectId: string }>(
      {
        query: (input) => ({
          url: `/project/${input.projectId}/checklist/createdByUser/`,
          method: "GET",
        }),
        providesTags: ["Checklist"],
      },
    ),
    deleteChecklist: builder.mutation<IChecklist, { projectId: string; checklistId: string }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/checklist/${input.checklistId}/`,
          method: "PUT",
        };
      },
      invalidatesTags: ["Checklist"],
    }),
    createChecklistFromImportedExcel: builder.mutation<IChecklist, IChecklistImportExcelHttp>({
      query(input) {
        return {
          url: `/project/${input.params.projectId}/checklist/import/excel`,
          method: "POST",
          body: input.body,
        };
      },
      invalidatesTags: ["Checklist"],
    }),
    updateChecklistIds: builder.mutation<
      IChecklistParts,
      {
        projectId: string;
        checklistId: string;
      }
    >({
      query(input) {
        return {
          url: `/project/${input.projectId}/checklist/${input.checklistId}/updatechecklistids`,
          method: "PUT",
        };
      },
      invalidatesTags: ["Checklist"],
    }),
    getChecklistsGeneratedFromTemplate: builder.query<IChecklist[], { projectId: string; templateId: number }>({
      query(input) {
        return {
          url: `/project/${input.projectId}/checklist/templates/checklists.from.template.get`,
          method: "POST",
          body: {
            templateId: input.templateId,
          },
        };
      },
      providesTags: ["Checklist"],
    }),
    createChecklist: builder.mutation<IChecklist, { projectId: string } & IAddChecklistBody>({
      query(input) {
        const { projectId, ...body } = input;
        return {
          url: `/project/${input.projectId}/checklist/add`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Checklist"],
    }),
    toggleCategoryInactive: builder.mutation<
      IChecklistParts,
      { projectId: string; checklistId: string; categoryId: string; inactive: boolean }
    >({
      query: (input) => ({
        url: `/project/${input.projectId}/checklist/${input.checklistId}/category/${input.categoryId}/inactive`,
        method: "PUT",
        body: { inactive: input.inactive },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        let resp: { data: IChecklistParts };
        try {
          resp = await queryFulfilled;
        } catch (error) {
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av kategori"));
          return;
        }

        const { checklist, items, categories } = resp.data;

        dispatch(updateChecklistProgressInRedux(checklist.progress));
        dispatch(setCurrentChecklistInRedux(checklist));
        dispatch(setChecklistItemsInRedux(sortArrayByNumber(items, "itemId")));
        dispatch(setChecklistCategoriesInRedux(sortArrayByNumber(categories, "categoryId")));
      },
    }),
    getAllChecklistTemplates: builder.query<IChecklistPopulated[], { projectId: string }>({
      query: (input) => {
        return {
          url: `/project/${input.projectId}/checklist/templates`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useGetProjectChecklistsQuery,
  useGetSingleChecklistQuery,
  useGetUniqueChecklistTagsQuery,
  useGetActiveProjectChecklistsCreatedByLoggedInUserQuery,
  useDeleteChecklistMutation,
  useCreateChecklistFromImportedExcelMutation,
  useUpdateChecklistIdsMutation,
  useGetChecklistsGeneratedFromTemplateQuery,
  useCreateChecklistMutation,
  useGetProjectChecklistTemplatesQuery,
  useToggleCategoryInactiveMutation,
  useGetAllChecklistTemplatesQuery,
} = checklistRTK;
